/**
 * RetakeConfirmModal
 *
 * Single retake-confirmation surface for ALL heroes (CheckUp, Care,
 * QultureUp, TelePortal Setup). Replaces the standalone
 * `HeroRetakeModal` (Radix `AlertDialog`) — see the 2026-04-28 UX
 * review §B-2 for the full rationale.
 *
 * The wrapper composes the existing `ConfirmModal` chassis with:
 *   • `intent='retake'`     — amber/RefreshCw visual taxonomy
 *   • `useRetakeGuide()`    — atomic server mutation (archive prior
 *                              result + reset guide-state in one tx)
 *   • per-hero copy via i18n keys + `{{hero}}` interpolation
 *   • Keep / Lose visualizer — promotes the consequence-clarity that
 *                              ConfirmModal already does for Restart /
 *                              Discard. Retake archives the prior
 *                              result; the user keeps it (in history)
 *                              but loses their active score / badge.
 *   • Arming animation      — same 1s sweep as Restart / Discard so
 *                              the safety bar is uniform across the
 *                              whole lifecycle suite.
 *
 * Old surface kept around as `HeroRetakeModal` (deprecation comment
 * + delegation to this component) so existing call sites work for
 * one release; we delete it in the next sweep.
 *
 * Usage:
 *
 *   const [open, setOpen] = useState(false);
 *
 *   <Button onClick={() => setOpen(true)}>Retake</Button>
 *   <RetakeConfirmModal
 *     open={open}
 *     onOpenChange={setOpen}
 *     flowId="checkup"
 *     journeyInstanceId={journeyId}
 *     onAfterRetake={() => navigate('/checkup/assessment')}
 *   />
 */

import { useTranslation } from '@tonnie/locale';
import { useRetakeGuide } from '@tonnie/sdk';
import { useState } from 'react';

import { ConfirmModal } from './lifecycle-modals';
import type { ConfirmModalConfig, FlowId, KeepLoseItem } from './types';

interface RetakeConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flowId: FlowId;
  journeyInstanceId: string | undefined;
  /**
   * Optional override for the modal title key. Defaults to
   * `copilot.lifecycle.retake.titleHero` ("Retake your {{hero}}?").
   * Hosts pass a custom key when the body of the i18n entry must
   * reference a domain-specific noun (e.g. QultureUp's "level test").
   */
  titleKey?: string;
  /**
   * Optional override for the modal body key. Defaults to a per-hero
   * mapping (`copilot.lifecycle.retake.body{Hero}`). Override only
   * when the consequence framing is genuinely different (TelePortal
   * preserves the active task list during the retake — that needs
   * spelling out).
   */
  bodyKey?: string;
  /** Optional override for the confirm-button label. */
  confirmLabelKey?: string;
  /**
   * Items the user keeps after retake. Defaults to "your previous
   * result is archived" — hosts can extend (e.g. QultureUp also
   * keeps mission progress, Passport miles, etc.).
   */
  keep?: KeepLoseItem[];
  /**
   * Items the user loses. Defaults to "your active score & badge."
   */
  lose?: KeepLoseItem[];
  /**
   * Called AFTER the retake mutation succeeds. Typical use: navigate
   * the user to the assessment URL.
   */
  onAfterRetake: () => void;
}

const DEFAULT_KEEP: KeepLoseItem[] = [
  {
    labelKey: 'copilot.lifecycle.retake.keep.archived',
  },
];

const DEFAULT_LOSE: KeepLoseItem[] = [
  {
    labelKey: 'copilot.lifecycle.retake.lose.activeScore',
  },
];

/**
 * Per-hero default body keys. Centralises the mapping so a host that
 * uses sane defaults doesn't have to thread `bodyKey` through.
 */
const DEFAULT_BODY_KEY_BY_FLOW: Record<FlowId, string> = {
  checkup: 'copilot.lifecycle.retake.bodyCheckup',
  care_assessment: 'copilot.lifecycle.retake.bodyCare',
  qultureup: 'copilot.lifecycle.retake.bodyQultureup',
  teleportal_setup: 'copilot.lifecycle.retake.bodyTeleportalSetup',
  // Journey Builder doesn't support retake; the registry rejects it
  // server-side. We keep a key here so the type is exhaustive but
  // the modal should never be opened for this flow.
  journey_builder: 'copilot.lifecycle.retake.body',
};

/**
 * Hero label key used to interpolate `{{hero}}` into the title.
 */
const HERO_LABEL_KEY_BY_FLOW: Record<FlowId, string> = {
  checkup: 'copilot.lifecycle.hero.checkup',
  care_assessment: 'copilot.lifecycle.hero.care_assessment',
  qultureup: 'copilot.lifecycle.hero.qultureup',
  teleportal_setup: 'copilot.lifecycle.hero.teleportal_setup',
  journey_builder: 'copilot.lifecycle.hero.journey_builder',
};

export function RetakeConfirmModal({
  open,
  onOpenChange,
  flowId,
  journeyInstanceId,
  titleKey,
  bodyKey,
  confirmLabelKey,
  keep,
  lose,
  onAfterRetake,
}: RetakeConfirmModalProps) {
  const { t } = useTranslation();
  const retakeMutation = useRetakeGuide();
  const [submitting, setSubmitting] = useState(false);

  // Resolve hero noun for {{hero}} interpolation. Same pattern as the
  // shared FlowLifecycleManager — hosts don't pass strings, the
  // modal pulls them from the i18n registry.
  const heroLabel = t(HERO_LABEL_KEY_BY_FLOW[flowId]);

  const config: ConfirmModalConfig = {
    titleKey: titleKey ?? 'copilot.lifecycle.retake.titleHero',
    bodyKey: bodyKey ?? DEFAULT_BODY_KEY_BY_FLOW[flowId],
    confirmLabelKey: confirmLabelKey ?? 'copilot.lifecycle.retake.confirm',
    cancelLabelKey: 'copilot.lifecycle.stay',
    arming: true,
    intent: 'warning',
    reversible: false,
    keep: keep ?? DEFAULT_KEEP,
    lose: lose ?? DEFAULT_LOSE,
  };

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await retakeMutation.mutateAsync({
        flowId,
        journeyId: journeyInstanceId,
        archivePreviousResult: true,
      });
      onOpenChange(false);
      onAfterRetake();
    } catch (err) {
      // The SDK hook already invalidates caches on conflict; we keep
      // the modal open so the user can retry. Errors that aren't
      // 409-conflict bubble to the host's error boundary via the
      // mutation's onError plumbing.
      console.warn('[RetakeConfirmModal] retake failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmModal
      open={open}
      config={config}
      context={{ hero: heroLabel }}
      busy={submitting}
      destructive
      intent="retake"
      onConfirm={handleConfirm}
      onCancel={() => !submitting && onOpenChange(false)}
    />
  );
}
